import type {
	RegionReport,
	RoomStat,
	Typology
} from '~~/core/ports/repositories';
import type { RouteLocationRaw } from 'vue-router';

import {
	PropertyTypeEnum,
	PropertyTypePlurals,
	propertyTypesTranslations
} from '~/core/entities/CRMEnums';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { CACHE_TIME, getCacheAsyncDataConfig } from '~/utils';
import { grammarInflection } from '~~/utils';
import { computed } from 'vue';

// Room categories mapping
const roomCategories = {
	bedrooms: { singular: 'quarto', plural: 'quartos' },
	suites: { singular: 'suíte', plural: 'suítes' },
	parkingSpots: { singular: 'vaga', plural: 'vagas' },
	bathrooms: { singular: 'banheiro', plural: 'banheiros' }
};

const roomTypePriority = {
	bedrooms: 1,
	suites: 2,
	parkingSpots: 3,
	bathrooms: 4
};

export interface RoomOption {
	roomType: string;
	amount: number;
	label: string;
	displayLabel: string;
	route: RouteLocationRaw;
}

export interface PropertyTypeColumn {
	type: string;
	title: string;
	singularTitle: string;
	roomOptions: RoomOption[];
	hasOptions: boolean;
}

export async function useRoomTypologies({
	regions,
	city,
	state
}: {
	regions: string[] | Ref<string[]>;
	city: string | Ref<string>;
	state: string | Ref<string>;
}) {
	const regionsRef = isRef(regions) ? regions : ref(regions);
	const cityRef = isRef(city) ? city : ref(city);
	const stateRef = isRef(state) ? state : ref(state);

	const key = computed(() => {
		return `region-properties-linking-by-room-${regionsRef.value.join('-')}-${cityRef.value}-${stateRef.value}`;
	});

	const { data: regionReport } = await useAsyncData<RegionReport[] | null>(
		key.value,
		() => {
			if (!regionsRef.value || !cityRef.value || !stateRef.value) {
				return Promise.resolve(null);
			}

			const fetcher = $fetch as any;
			return fetcher('/api/region/report', {
				query: {
					regions: regionsRef.value,
					city: cityRef.value,
					state: stateRef.value,
					includeTypologies: true
				}
			}) as Promise<RegionReport[] | null>;
		},
		{
			default: () => null,
			server: true,
			watch: [key],
			...getCacheAsyncDataConfig<RegionReport[] | null>(CACHE_TIME)
		}
	);

	const regionData = computed<RegionReport | undefined>(() => {
		return regionReport.value?.[0];
	});

	const propertyTypeColumns = computed<PropertyTypeColumn[]>(() => {
		const typologies = regionData.value?.typologies || [];

		return typologies
			.map(typology => {
				const propertyType = typology.propertyType;

				const displayInfo = {
					title:
						PropertyTypePlurals[propertyType as PropertyTypeEnum] ||
						propertyType,
					singularTitle:
						propertyTypesTranslations[propertyType as PropertyTypeEnum] ||
						propertyType
				};

				const roomOptions = generateRoomOptions(
					typology,
					propertyType,
					displayInfo,
					regionData.value
				);

				return {
					type: propertyType,
					title: displayInfo.title,
					singularTitle: displayInfo.singularTitle,
					roomOptions: roomOptions.slice(0, 4),
					hasOptions: roomOptions.length > 0
				};
			})
			.filter(column => column.hasOptions)
			.slice(0, 4); // Limit to 4 property types
	});

	return {
		propertyTypeColumns,
		roomCategories
	};
}

/**
 * Generates room options for a specific property type
 */
function generateRoomOptions(
	typology: Typology,
	propertyType: string,
	displayInfo: { title: string; singularTitle: string },
	regionData: RegionReport | undefined
): RoomOption[] {
	if (!regionData) return [];

	const roomOptions: RoomOption[] = [];

	Object.entries(roomCategories).forEach(([roomKey, roomNames]) => {
		const roomStats =
			typology.roomStats?.[roomKey as keyof typeof typology.roomStats] || [];

		const validOptions = roomStats
			.filter((stat: RoomStat) => stat.propertiesCount > 0 && stat.amount > 0)
			.sort((a: RoomStat, b: RoomStat) => a.amount - b.amount);

		validOptions.forEach((stat: RoomStat) => {
			const route = PropertyFiltersPresenter.getRouteObjectFromFilters({
				states: [regionData.state],
				cities: [regionData.city],
				regions: [regionData.region],
				propertyTypes: [propertyType as PropertyTypeEnum],
				[roomKey]: [stat.amount]
			});

			const simpleLabel = `${displayInfo.title} com ${stat.amount} ${grammarInflection(
				stat.amount,
				roomNames.singular,
				roomNames.plural
			)}`;

			roomOptions.push({
				roomType: roomKey,
				amount: stat.amount,
				label: `${displayInfo.singularTitle} com ${stat.amount} ${grammarInflection(
					stat.amount,
					roomNames.singular,
					roomNames.plural
				)}`,
				displayLabel: simpleLabel,
				route
			});
		});
	});

	return roomOptions.sort((a: RoomOption, b: RoomOption) => {
		const typeA =
			roomTypePriority[a.roomType as keyof typeof roomTypePriority] || 5;
		const typeB =
			roomTypePriority[b.roomType as keyof typeof roomTypePriority] || 5;

		if (typeA !== typeB) return typeA - typeB;

		return a.amount - b.amount;
	});
}
